import * as API from '@/api/endpoints';
import * as Repository from '@/db/repositories';
import { DBMetadataKey, isDBEntryStale, setDBUpdatedNow } from '@/db/helpers/dbMetadata';

const genericSync = async <T>(
  apiFn: () => Promise<T[]>,
  // eslint-disable-next-line no-unused-vars
  dbRepo: { bulkAdd: (items: T[]) => Promise<T[]> },
  dbKey: DBMetadataKey,
  options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> },
) => {
  const { forceUpdate = false } = options ?? {};

  // we handle offline through the axios client controller

  const isStale = await isDBEntryStale(dbKey, options?.metadataDict);
  if (!isStale && !forceUpdate) return;

  try {
    const data = await apiFn();
    await dbRepo.bulkAdd(data);
    await setDBUpdatedNow(dbKey);
  } catch (error) {
    console.error(`Failed to sync ${dbKey} from API:`, error);
    // TODO: Save error to ErrorLogRepo
    throw error;
  }
};

// ---- Sync functions ----
export const syncOrderNotificationEvents = async (typeIds: number[], options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  if (!typeIds.length) return;
  const isStale = await isDBEntryStale('getOrderNotificationEvents', options?.metadataDict);
  const cached = await Promise.all(typeIds.map((id) => Repository.NotificationEventTypeDB.list(id)));
  if (!isStale && !options?.forceUpdate && cached.every((events) => events.length > 0)) return;

  const eventOptions = await Promise.all(typeIds.map((typeId) => API.getNotificationEventTypeForDropdown(typeId)));
  if (eventOptions.some((items, index) => !items.length || items.some((item) => item.parentId !== typeIds[index]))) {
    throw new Error('Notification lookup data is unavailable.');
  }
  await Repository.NotificationEventTypeDB.replace(typeIds, eventOptions.flat());
  await setDBUpdatedNow('getOrderNotificationEvents');
};

export const syncTournaments = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getTournaments, Repository.TournamentDB, 'getTournaments', options);
};

export const syncOrderAccountsForDropdown = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getOrderAccountsForDropdown, Repository.OrderAccountsForDropdownDB, 'getOrderAccountsForDropdown', options);
};

export const syncOrderStatusList = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getOrderStatusList, Repository.OrderStatusListDB, 'getOrderStatusList', options);
};

export const syncBuildLocationsForDropdown = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getBuildLocationsForDropdown, Repository.BuildLocationsForDropdownDB, 'getBuildLocationsForDropdown', options);
};

export const syncAppUsersForDropdown = async (options?: { forceUpdate?: boolean; metadataDict?: Record<string, string> }) => {
  return genericSync(API.getAppUsersForDropdown, Repository.AppUserForDropdownDB, 'getAppUsersForDropdown', options);
};

// ---- Data functions ----

export const getOrderAccountsForDropdown = async () => {
  await syncOrderAccountsForDropdown();
  return Repository.OrderAccountsForDropdownDB.list();
};

export const getOrderStatusList = async () => {
  await syncOrderStatusList();
  return Repository.OrderStatusListDB.list();
};

export const getBuildLocationsForDropdown = async () => {
  await syncBuildLocationsForDropdown();
  return Repository.BuildLocationsForDropdownDB.list();
};

export const getAppUsersForDropdown = async () => {
  await getAppUsersForDropdown();
  return Repository.AppUserForDropdownDB.list();
};
